import { useContext, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthProvider';
import { AWS_CONFIG_TYPE } from '../BaseUrl';
import completeAzureSession from './completeAzureSession';
import LoadingIcon from '../reusable-component/LoadingIcon';

/**
 * Runs on every load: if the app returned from Azure (loginRedirect), process #code=... and finish login.
 * Must be mounted once under MsalProvider + AuthProvider + SnackbarProvider.
 */
export default function AzureSsoCallback() {
  const { instance } = useMsal();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {
    setUser,
    setToken,
    setUserId,
    setUserGroup,
    setLLMId,
    setLLMName,
  } = useContext(AuthContext);

  /** Stops a second effect run (Strict Mode / dependency churn) from starting another session while the first may still be awaiting permissions. */
  const redirectSessionLockRef = useRef(false);
  const [ssoFinishing, setSsoFinishing] = useState(false);

  useEffect(() => {
    if (AWS_CONFIG_TYPE) return;

    instance
      .handleRedirectPromise()
      .then((response) => {
        if (!response) return;
        if (redirectSessionLockRef.current) return;
        redirectSessionLockRef.current = true;
        setSsoFinishing(true);
        return completeAzureSession({
          instance,
          redirectResult: response,
          navigate,
          enqueueSnackbar,
          setUser,
          setToken,
          setUserId,
          setUserGroup,
          setLLMId,
          setLLMName,
        })
          .catch((err) => {
            console.error('Azure session completion failed:', err);
            redirectSessionLockRef.current = false;
            enqueueSnackbar('Azure AD login failed', { variant: 'error' });
          })
          .finally(() => {
            setSsoFinishing(false);
          });
      })
      .catch((err) => {
        console.error('SSO redirect handling failed:', err);
        enqueueSnackbar('Azure AD login failed', { variant: 'error' });
      });
    // Redirect handling only depends on the MSAL instance; avoid re-running when context/snackbar identities change.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: single subscribe per instance
  }, [instance]);

  return ssoFinishing ? <LoadingIcon /> : null;
}
